/**
 * Soren Confirm Answer Card.
 * Renders prepared write actions:
 * "Nothing is saved until you tap Confirm."
 */

import { useState } from 'react'

export interface ConfirmCardData {
  title: string
  subtitle: string
  ruleNotice: string
  successMessage?: string
  fields: {
    label: string
    value: string
    wide?: boolean
    isMoney?: boolean
  }[]
  onConfirm: () => Promise<void> | void
  onCancel: () => void
}

export default function SorenConfirmAnswerCard({
  data,
  isConfirmed = false,
  isCancelled = false,
  onConfirmedChange,
}: {
  data: ConfirmCardData
  isConfirmed?: boolean
  isCancelled?: boolean
  onConfirmedChange?: (confirmed: boolean) => void
}) {
  const [confirmed, setConfirmed] = useState(isConfirmed)
  const [cancelled, setCancelled] = useState(isCancelled)
  const [busy, setBusy] = useState(false)
  const closed = confirmed || cancelled

  async function handleConfirm() {
    if (closed) return
    setBusy(true)
    try {
      await data.onConfirm()
      setConfirmed(true)
      onConfirmedChange?.(true)
    } finally {
      setBusy(false)
    }
  }

  function handleCancel() {
    if (closed || busy) return
    setCancelled(true)
    data.onCancel()
  }

  return (
    <article className="answer-card" data-testid="soren-confirm-card">
      <div className="confirm-intro">
        <h3>{data.title}</h3>
        <p>{data.subtitle}</p>
      </div>

      <div className="confirm-fields">
        {data.fields.map((f, i) => (
          <div key={i} className={`field ${f.wide ? 'wide' : ''}`}>
            <label>{f.label}</label>
            <input
              readOnly
              className={f.isMoney ? 'money' : ''}
              value={f.value}
            />
          </div>
        ))}
      </div>

      <div className="confirm-rule">
        <b>Nothing is saved until you tap Confirm.</b> {data.ruleNotice}
      </div>

      {confirmed && (
        <div className="success show" data-confirm-success data-testid="soren-confirm-success">
          {data.successMessage || 'Confirmed. Action recorded successfully.'}
        </div>
      )}

      <div className="actions">
        <button
          className="btn primary"
          type="button"
          data-confirm
          data-testid="soren-btn-confirm"
          disabled={closed || busy}
          onClick={handleConfirm}
        >
          {confirmed ? 'Confirmed' : cancelled ? 'Cancelled' : busy ? 'Saving…' : 'Confirm'}
        </button>
        {!closed && (
          <button
            className="btn"
            type="button"
            data-cancel
            data-testid="soren-btn-cancel"
            disabled={busy}
            onClick={handleCancel}
          >
            Cancel
          </button>
        )}
      </div>
    </article>
  )
}
